// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import {
  DocBoxDocument,
  DocBoxDocumentModel,
  DocBoxService,
} from '../../services/docBox.service';

const sortPrios = ['startDate', 'endDate'] as const;

const topDocCount = 3;

export function docBoxPanelSort(
  a: DocBoxDocumentModel['documents'][0],
  b: DocBoxDocumentModel['documents'][0]
): number {
  for (const prio of sortPrios) {
    const diff =
      (b[prio] ? Date.parse(b[prio] || '') : 0) -
      (a[prio] ? Date.parse(a[prio] || '') : 0);
    // in case diff === 0 we move to the next prio
    if (diff !== 0) {
      return diff;
    }
  }
  // equal
  return 0;
}

@Component({
  selector: 'app-doc-box-panel',
  templateUrl: './docBoxPanel.component.html',
  styleUrls: ['./docBoxPanel.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    APICallGateComponent,
    NgFor,
    LocalizedDatePipe,
    AsyncPipe,
    TranslateModule,
  ],
})
export class DocBoxPanelComponent implements OnChanges {
  @Input() systemMaterialNumber?: number;
  @Input() systemSerialNumber?: string;
  @Input() modalityId?: number;

  protected docBoxEntries = this.docBoxService.docBoxEntries;

  downloads = new Map<
    string,
    ReturnType<typeof this.docBoxService.requestLifecycle>
  >();

  protected docs$ = this.docBoxEntries.lastSuccessfulData$;

  protected topDocs$ = this.docs$.pipe(
    // pick top tickets
    map((docs) => {
      if (docs) {
        const sorted = [...docs.documents].sort(docBoxPanelSort);
        return sorted.slice(0, topDocCount);
      }
      return [];
    })
  );

  protected topDocCountDisplay$ = combineLatest([
    this.docs$,
    this.topDocs$,
  ]).pipe(
    map(([{ documents: docs }, topDocs]) => {
      if (docs.length > 0) {
        return '(' + topDocs.length + '/' + docs.length + ')';
      } else {
        return '';
      }
    })
  );

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private docBoxService: DocBoxService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.systemMaterialNumber) {
      console.error('input systemMaterialNumber is undefiend');
      return;
    }
    if (!this.systemSerialNumber) {
      console.error('input systemSerialNumber is undefiend');
      return;
    }
    if (!this.modalityId) {
      console.error('input modalityId is undefiend');
      return;
    }
    if (
      changes['systemSerialNumber'] ||
      changes['systemMaterialNumber'] ||
      changes['modalityId']
    ) {
      this.docBoxEntries.request({
        systemSerialNumber: this.systemSerialNumber,
        systemMaterialNumber: this.systemMaterialNumber,
        modalityId: this.modalityId,
      });
    }
  }

  navigateToDocBox(): void {
    const url = this.router
      .createUrlTree([
        'system',
        this.systemMaterialNumber,
        this.systemSerialNumber,
        'docbox',
      ])
      .toString();
    this.router.navigateByUrl(url);
  }

  // trigger download of a list of docs
  download(
    docs: DocBoxDocument[]
  ): ReturnType<typeof this.docBoxService.requestLifecycle> {
    const request = this.docBoxService.requestLifecycle(
      docs.map((doc) => doc.identifier)
    );

    docs.forEach((doc) => {
      this.downloads.set(doc.identifier, request);
    });

    request.lastSuccessfulData$
      .pipe(
        filter((result) => !!result),
        first()
      )
      .subscribe((result) => window.open(result.downloadLink, '_blank'));
    return request;
  }
}
